import { ComponentProps, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { lastValueFrom } from 'rxjs';
import { useFormInputState } from '@/hooks/useFormInputState';
import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { formatCountriesList, getSubLocale } from '@/utils/locales';

import CountrySelectorV2 from '@/components/common/form/fields/specificFields/CountrySelectorV2';
import FormErrorV2 from '@/components/common/form/FormErrorV2';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './FormFieldV2.module.scss';
import stylesComponent from './PhoneFieldV2.module.scss';
import { ERROR_TYPE, FIELD_TYPE, VALIDATION } from '@/constants/form';
import { URLS } from '@/constants/urls';

export type PhoneFieldV2Props = {
  id?: string;
  label?: string;
  labelIndicator?: string;
  validators?: string[];
  isWithCountrySelector?: boolean;
  hintMessage?: string;
  rest?: Record<string, any>;
  country?: string;
  countryPhone?: string;
} & ComponentProps<'input'>

const PhoneFieldV2 = (props: PhoneFieldV2Props): React.JSX.Element => {
  const {
    id,
    name,
    country,
    countryPhone,
    label,
    labelIndicator,
    required,
    disabled,
    isWithCountrySelector = true,
    hintMessage,
    ...rest
  } = props;

  const { register, formState: { errors }, clearErrors, setValue } = useFormContext();
  const { isFilled } = useFormInputState({ name });
  const { phoneValidatorService } = useServices();

  const locale = useAppSelector(({ root }) => root.storeConf.locale);

  const [selectedCountry, setSelectedCountry] = useState(countryPhone || country);
  const [isFocused, setIsFocused] = useState(false);

  const labelClassName = [
    styles.label,
    (isFilled || isFocused) ? styles['label-visible'] : styles['label-invisible']
  ].filter(Boolean).join(' ');

  const isPhoneValidated = async (newNumber) => {
    const parsedNumber = await lastValueFrom(
      phoneValidatorService.parsePhoneNumber(newNumber, selectedCountry)
    );

    if (!parsedNumber?.internationalNumber && newNumber) {
      return false;
    }
    clearErrors(name);
    return true;
  };

  const onCountryChange = async (newCountry) => {
    setSelectedCountry(newCountry);
  };

  const onPhoneNumberChange = async (e) => {
    const parsedNumber = await lastValueFrom(
      phoneValidatorService.parsePhoneNumber(e.target.value, selectedCountry)
    );

    if (parsedNumber?.internationalNumber && e.target.value) {
      setValue(name, parsedNumber.internationalNumber);
    }
  };

  const validate = Object.fromEntries([
    [ERROR_TYPE.PHONE, isPhoneValidated]
  ]);

  useEffect(() => {
    const countryCode = countryPhone || country || getSubLocale(locale);

    const fetchCountry = async () => {
      const [_favorites, _others, selectedCountry] = await formatCountriesList(locale, countryCode);

      setSelectedCountry(selectedCountry?.value);
    };

    fetchCountry();
  }, []);

  return (
    <div>
      <div className={stylesComponent.container}>
        {isWithCountrySelector && (
          <div className={stylesComponent['select-container']}>
            <CountrySelectorV2
              id="ti-address-form-country-phone"
              locale={locale}
              country={countryPhone || country}
              autoComplete="shipping country"
              name="countryPhone"
              label={labelIndicator}
              onChange={onCountryChange}
              rbf={`${id}-country`}
            />
          </div>
        )}
        <div className={`${stylesComponent['input-container']} ${styles.container}`}>
          <label className={labelClassName}>{label}</label>
          {errors?.[name] && (
            <Icon
              src={URLS.DELETE_CIRCLE_ICON_RED}
              alt="arrow"
              width={20}
              height={20}
              className={styles['icon']}
            />
          )}
          <input
            id={id}
            data-rbf={`input${id ? '-' + id : ''}`}
            type={FIELD_TYPE.PHONE}
            placeholder={label}
            disabled={disabled}
            className={[
              styles.input,
              (isFilled || isFocused) && styles['input-focus'],
              errors?.[name] && styles['input-error']
            ].filter(Boolean).join(' ')}
            name={name}
            {...register(name, {
              required,
              pattern: {
                value: VALIDATION.PHONE_REGEX,
                message: 'forms.validPhone'
              },
              validate,
              onChange: onPhoneNumberChange
            })}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...rest}
          />
        </div>
      </div>
      {hintMessage && <div className={styles.hint}>{hintMessage}</div>}
      {errors?.[name] && <FormErrorV2 error={errors[name]} />}
    </div>
  );
};

export default PhoneFieldV2;
